/**
 * 워크스페이스 관리 API — 관리 콘솔 "워크스페이스 관리".
 *
 * chat-server `/api/v1/admin/workspace/*`.
 *
 * ── 통합설정 › 회사 관리 (2026-08-25) ─────────────────────────────────────────
 * 관리 콘솔(8444)의 같은 이름 api 모듈을 **그대로 옮겨 온 것**이다. 함수 이름·인자·반환 모양이
 * 한 글자도 다르지 않다 — 두 화면이 같은 서버 라우트를 부르므로 원본과 대조하기 쉽게 둔다.
 * 콘솔은 BFF `/api` + 이 상대 경로, 8443 은 baseURL 이 이미 `/api/v1` 이라 **같은 문자열이
 * 같은 라우트에 닿는다**(서버·BFF 변경 0). 권한은 chat-server 가 ADMIN/SYSTEM_ADMIN 으로 판정.
 *
 * 용량 단위는 전부 **바이트**다. 화면 입력은 GB 로 받고 여기서 환산한다.
 * limitBytes = -1 은 무제한을 뜻한다.
 */
import { apiClient } from './client';

export type QuotaScopeType = 'personal' | 'team' | 'company';
export type FileKind = 'all' | 'word' | 'excel' | 'ppt' | 'etc';

export interface WorkspaceOverview {
  totalBytes: number;
  fileCount: number;
  company: { limitBytes: number; unlimited: boolean; pct: number };
  byKind: Array<{ kind: string; bytes: number; count: number }>;
  topUploaders: Array<{ userId: string; name: string; email: string; bytes: number; count: number }>;
}

export interface UsageRow {
  scopeId: string;
  name: string;
  /** 개인=이메일 / 팀=본부명 */
  subtitle: string;
  usedBytes: number;
  limitBytes: number;
  fileCount: number;
  pct: number;
  unlimited: boolean;
  warn: boolean;
  /** 한도를 따로 지정하지 않아 기본값이 적용 중인가. */
  isDefault: boolean;
  // 개인 전용
  position?: string | null;
  /**
   * 계정이 살아 있는가 — **개인·팀 둘 다** 이 칸을 쓴다 (2026-08-31).
   *
   * ⚠ 종전에는 개인 행만 `status` 를 받아 그것으로 「안 쓰는 계정」을 그렸다. 그런데
   *   `User.status` 는 **접속 상태**(`online|away|offline`)라, 접속 안 한 사람이 전부
   *   비활성으로 보이고 배지에 영문 `offline` 이 그대로 떴다(운영 11행 전부).
   *   서버가 이제 개인 행에도 `isActive` 를 준다 — **옛 서버는 안 주므로 옵셔널**이고,
   *   그때는 「안 쓰는 계정」 표시가 붙지 않을 뿐이다(잘못 붙는 것보다 낫다).
   */
  isActive?: boolean;
  // 팀 전용
  memberCount?: number;
}

export interface UsageResult {
  scope: 'personal' | 'team';
  defaultLimitBytes: number;
  rows: UsageRow[];
}

export interface AdminWorkspaceFile {
  id: string;
  name: string;
  docTitle: string | null;
  mimeType: string | null;
  kind: string;
  sizeBytes: number;
  ownerScope: string | null;
  ownerTeamId: string | null;
  verified: boolean;
  createdAt: string;
  uploader: { id: string; name: string; email: string };
}

export interface QuotaDefaults {
  personal: number;
  team: number;
  company: number;
  unlimitedSentinel: number;
}

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export async function getWorkspaceOverview(): Promise<WorkspaceOverview> {
  const { data } = await apiClient.get<WorkspaceOverview>('/admin/workspace/overview');
  return {
    ...data,
    byKind: asArray(data?.byKind),
    topUploaders: asArray(data?.topUploaders),
  };
}

export async function getWorkspaceUsage(scope: 'personal' | 'team'): Promise<UsageResult> {
  const { data } = await apiClient.get<UsageResult>('/admin/workspace/usage', { params: { scope } });
  return { ...data, rows: asArray<UsageRow>(data?.rows) };
}

export async function listWorkspaceFiles(params: {
  q?: string; kind?: FileKind; scope?: 'all' | QuotaScopeType; limit?: number; cursor?: string;
}): Promise<{ files: AdminWorkspaceFile[]; nextCursor: string | null }> {
  const clean: Record<string, string | number> = {};
  if (params.q) clean.q = params.q;
  if (params.kind && params.kind !== 'all') clean.kind = params.kind;
  if (params.scope && params.scope !== 'all') clean.scope = params.scope;
  if (params.limit) clean.limit = params.limit;
  if (params.cursor) clean.cursor = params.cursor;
  const { data } = await apiClient.get<{ files: AdminWorkspaceFile[]; nextCursor: string | null }>(
    '/admin/workspace/files', { params: clean },
  );
  return { files: asArray<AdminWorkspaceFile>(data?.files), nextCursor: data?.nextCursor ?? null };
}

export async function setWorkspaceQuota(body: {
  scopeType: QuotaScopeType;
  scopeId?: string;
  limitBytes: number;
}): Promise<UsageRow & { scopeType: QuotaScopeType }> {
  const { data } = await apiClient.patch('/admin/workspace/quota', body);
  return data;
}

export async function getQuotaDefaults(): Promise<QuotaDefaults> {
  const { data } = await apiClient.get<QuotaDefaults>('/admin/workspace/defaults');
  return data;
}

// ─── 용량 표기 헬퍼 ────────────────────────────────────────────────
const GB = 1024 * 1024 * 1024;

/** 바이트 → 사람이 읽는 표기. -1 은 호출부가 "무제한" 으로 분기한다. */
export function formatBytes(bytes: number): string {
  if (bytes < 0) return '무제한';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)}${units[i]}`;
}

export function bytesToGb(bytes: number): number {
  return Math.round((bytes / GB) * 100) / 100;
}

export function gbToBytes(gb: number): number {
  return Math.round(gb * GB);
}
